import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router'

import { listServices } from '../../api/services'
import type { Service } from '../../api/services'
import { usePortal } from '../../auth/sessionContext'
import { DataState } from '../../components/DataState'
import { Money } from '../../components/Money'
import { PageHeader } from '../../components/ui/PageHeader'
import { UNSET, formatCount } from '../../format'
import { PRODUCT_DOMAINS, domainForService } from '../../productDomains'
import { Ltr } from '../../components/ui/Ltr'
import { StatusChip } from '../../components/ui/StatusChip'
import { presentation } from '../../portalMode'

/**
 * The services this customer may call.
 *
 * The list comes from the server already filtered to what has been granted and
 * is in the current commercial scope, so this page holds no list of its own.
 * That is the whole safeguard against a dormant capability appearing here: the
 * portal cannot advertise something it was never told about.
 *
 * Grouped into the product families rather than listed flat, because a flat
 * list of twenty-one codes reads as a database table and a customer choosing a
 * service is thinking in families.
 */
export function ServicesPage() {
  const { membership } = usePortal()
  const view = presentation(membership)
  const customerId = membership.customer.id

  const query = useQuery({
    queryKey: ['client', 'services', customerId],
    queryFn: ({ signal }) => listServices(customerId, signal),
  })

  const services = query.data?.services ?? []
  const groups = PRODUCT_DOMAINS.map((domain) => ({
    domain,
    rows: services.filter(
      (service) => domainForService(service.domain, service.code).domainKey === domain.domainKey,
    ),
  })).filter((group) => group.rows.length > 0)

  return (
    <Box>
      <PageHeader
        title={view.isService ? 'سرویس‌های من' : 'دسترسی سرویس‌ها'}
        description={
          view.isService
            ? 'استعلام‌هایی که حساب شما اجازهٔ انجامشان را دارد، به همراه بهای هر استعلام. برای افزودن یا حذف سرویس با پشتیبانی تماس بگیرید.'
            : 'سرویس‌هایی که اپلیکیشن‌های شما اجازهٔ فراخوانی آن‌ها را دارند، به همراه بهای هر فراخوانی. برای افزودن یا حذف دسترسی با ما تماس بگیرید؛ دسترسی از سمت ما داده می‌شود.'
        }
        action={
          view.offersDeveloperDestinations ? (
            <Button component={RouterLink} to="/docs" size="small">
              راهنمای فنی
            </Button>
          ) : null
        }
      />

      <DataState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={services.length === 0}
        emptyMessage="هنوز به هیچ‌یک از اپلیکیشن‌های شما دسترسی سرویسی داده نشده است."
        onRetry={() => void query.refetch()}
      >
        <Stack spacing={4}>
          {groups.map((group) => (
            <Box key={group.domain.domainKey} component="section">
              <Typography variant="h2" gutterBottom>
                {group.domain.labelFa}
              </Typography>
              <Stack spacing={2}>
                {group.rows.map((service) => (
                  <ServiceRow
                    key={service.code}
                    service={service}
                    technical={!view.isService}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DataState>
    </Box>
  )
}

/**
 * One service.
 *
 * A card rather than a table row: at 390px a four-column table either scrolls
 * sideways or crushes the Persian name into one word per line, and the price is
 * the column that ends up off-screen — which is the one a customer came to
 * read.
 */
function ServiceRow({ service, technical }: { service: Service; technical: boolean }) {
  const name = service.display_name_fa || service.name
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-start' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          {service.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
              {service.description}
            </Typography>
          ) : null}
        </Box>

        {/* The price is the second thing a customer looks for, so it is the
            second thing on the card and it is the largest text in this
            column. It used to be a caption beside a full-width monospace
            capability code, which made the identifier the loudest element on
            a page about what things cost. */}
        <Stack
          spacing={1}
          sx={{
            flexShrink: 0,
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            textAlign: { md: 'end' },
          }}
        >
          <Box>
            <Typography variant="caption" component="p" color="text.secondary">
              بهای هر فراخوانی
            </Typography>
            <Money amountIrr={service.sale_price_irr} whenUnknown={UNSET.salePrice} emphasis />
          </Box>
          <Button
            component={RouterLink}
            to={`/services/${service.code}`}
            variant="outlined"
            size="small"
            // Every card carries this link, so the visible words alone leave a
            // screen reader with a list of identical destinations. The name
            // says which service it opens.
            aria-label={`جزئیات و آزمایش ${name}`}
          >
            جزئیات و آزمایش
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {/* The technical footer: what a developer needs, at the weight a
          developer needs it. */}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap', alignItems: 'center' }}
      >
        <StatusChip status="active" />
        {/* A business customer never types this; a developer always does. */}
        {technical ? (
          <Ltr
            component="code"
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {service.code}
          </Ltr>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          در دسترس {formatCount(service.applications.length)} اپلیکیشن
        </Typography>
      </Stack>
    </Paper>
  )
}
